import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import Modal from 'react-bootstrap/lib/Modal';
import Button from 'react-bootstrap/lib/Button';
import classnames from 'classnames';

import { translate as t } from '../../lib/I18n';
import { KeyCodes } from '../../constants/AppConstants';
import ReactOverlayManager, {modalContainer} from '../../helpers/ReactOverlayManager';
import connectToStores from '../../helpers/connectToStores';
import DimensionsStore from '../../stores/DimensionsStore';

import Icon from './Icon';
import Page from './Page';
import BaseModalTracker from './BaseModalTracker';

const _getStateFromStores = () => ({
    isDesktop: DimensionsStore.isDesktopWidth(),
});

export const baseModalScrollTo = ({ left = null, top = null, behavior = 'smooth' }) => {
    const modalBody = document.querySelector('.modal-body');
    if (modalBody && modalBody.scrollTo) {
        modalBody.scrollTo({ left, top, behavior });
    }
};

const modalManager = new ReactOverlayManager();

export class BaseModal extends React.PureComponent {

    static propTypes = {
        show: PropTypes.bool,
        bodyAriaLabel: PropTypes.string,
        disableBlur: PropTypes.bool,
        disableBorderFooter: PropTypes.bool,
        disableEscape: PropTypes.bool,
        disableClose: PropTypes.bool,
        disableContinue: PropTypes.bool,
        showCloseIcon: PropTypes.bool,
        hideClose: PropTypes.bool,
        hideContinue: PropTypes.bool,
        hideHeader: PropTypes.bool,
        isAutofocusing: PropTypes.bool,
        onEnterContinue: PropTypes.bool,
        onOpen: PropTypes.func,
        onClose: PropTypes.func,
        onContinue: PropTypes.func,
        onKeyDown: PropTypes.func,
        className: PropTypes.string,
        modalDescription: PropTypes.string,
        titleText: PropTypes.string,
        titleComponent: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.func,
        ]),
        closeText: PropTypes.string,
        continueText: PropTypes.string,
        continueClassName: PropTypes.string,
        children: PropTypes.node,
        header: PropTypes.oneOfType([
            PropTypes.node,
            PropTypes.bool
        ]),
        footer: PropTypes.oneOfType([
            PropTypes.node,
            PropTypes.bool
        ]),
        footerContent: PropTypes.object,
        headerClass: PropTypes.string,
        headerIcon: PropTypes.element,
        useFlex: PropTypes.bool,
        backdropClassName: PropTypes.string,
        variant: PropTypes.oneOf(['light']),
        isDesktop: PropTypes.bool,
        modalTitleClass: PropTypes.string,
        showCloseFooterButton: PropTypes.bool,
        continueProps: PropTypes.object,
    };

    static defaultProps = {
        show: true,
        disableBlur: false,
        disableBorderFooter: false,
        disableEscape: false,
        disableClose: false,
        disableContinue: false,
        hideClose: false,
        hideContinue: false,
        hideHeader: false,
        isAutofocusing: true,
        onEnterContinue: true,
        onOpen: _.noop,
        onClose: _.noop,
        onContinue: _.noop,
        onKeyDown: _.noop,
        className: null,
        modalDescription: null,
        titleText: null,
        titleComponent: 'h1',
        closeText: 'button.text.close',
        continueText: 'button.text.continue',
        header: null,
        headerClass: '',
        headerIcon: null,
        footer: null,
        footerContent: null,
        useFlex: false,
        backdropClassName: null,
        bodyAriaLabel: null,
        variant: null,
        isDesktop: true,
        modalTitleClass: ''
    };

    uid = _.uniqueId('modalBody_');

    constructor(props) {
        super(props);
        if (props.show) {
            props.onOpen();
        }
        this.handleKeyPress = this.handleKeyPress.bind(this);
    }

    UNSAFE_componentWillReceiveProps(newProps) {
        if (newProps.show && !this.props.show) {
            newProps.onOpen();
        }
    }

    UNSAFE_componentWillMount() {
        window.addEventListener('keydown', this.handleKeyPress);
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.handleKeyPress);
    }

    componentDidMount() {
        _.defer(this.handleModalOpened);
    }

    handleModalOpened = () => {
        const { isAutofocusing } = this.props;
        const modalBody = document.getElementById(this.uid);

        if (modalBody) {
            const inputElt = modalBody.querySelector('button:not(:disabled), input, select');
            if (inputElt && isAutofocusing) {
                inputElt.focus();
            } else {
                modalBody.focus();
            }
        }
    }

    handleKeyPress(event) {
        const ignoreTarget = ['TEXTAREA', 'BUTTON'].includes(event.target.tagName);
        if (this.props.show && event.keyCode === KeyCodes.ENTER && !this.props.disableContinue && !ignoreTarget && this.props.onEnterContinue) {
            event.stopPropagation();
            _.defer(this.props.onContinue);
        }
    }

    _renderHeader({ header, headerIcon, titleText, titleComponent, closeText, onClose, isDesktop, hideClose, disableClose, headerClass, useFlex, modalTitleClass, showCloseIcon }) {
        const headerClassName = classnames(headerClass, { 'modal-header--flex': useFlex });
        const showCloseBtn = showCloseIcon || (!hideClose && !isDesktop);

        if (!header) {
            if (header === false) {
                return null;
            }

            return (
                <Modal.Header className={headerClassName}>
                    {headerIcon && <div className="modal-header__icon pull-left">
                        {headerIcon}
                    </div>}
                    {titleText &&
                        <Page title={t(titleText)} goBack={false}>
                            <Modal.Title id="modalTitle" componentClass={titleComponent} className={modalTitleClass}>
                                {t(titleText)}<span className="sr-only">. </span>
                            </Modal.Title>
                        </Page>
                    }

                    {showCloseBtn && (
                        <button
                            onClick={onClose}
                            disabled={disableClose}
                            className="modal-header__close-button"
                        >
                            <Icon icon="close" />
                            <span className="sr-only">{t(closeText)}</span>
                        </button>
                    )}
                </Modal.Header>
            );
        } else {
            return (
                <Modal.Header className={headerClassName}>
                    {header}
                </Modal.Header>
            );
        }
    }

    _renderFooter({ disableBorderFooter, disableContinue, isDesktop, hideClose, disableClose, continueText, continueClassName, closeText, onContinue, onClose, hideContinue, footer, footerContent, useFlex, continueProps = {}}) {
        const footerClassName = classnames({
            'modal-footer--flex': useFlex,
            'modal-footer--no-border': disableBorderFooter || (!isDesktop && !footer && hideContinue),
            'modal-footer--no-padding': !isDesktop && !footer && hideContinue,
        });

        if (!footer) {
            if (footer === false) {
                return null;
            }

            return (
                <Modal.Footer className={footerClassName}>

                    {((!hideClose && isDesktop) || this.props.showCloseFooterButton) && (
                        <Button
                            bsStyle="default"
                            onClick={onClose}
                            className="close action-btn action-btn--secondary pull-left"
                            disabled={disableClose}
                        >
                            {t(closeText)}
                        </Button>
                    )}

                    {footerContent}

                    {!hideContinue && (
                        <Button
                            bsStyle="primary"
                            onClick={onContinue}
                            className={classnames("continue action-btn action-btn--primary pull-right", continueClassName)}
                            disabled={disableContinue}
                            {...continueProps}
                        >
                            {t(continueText)}
                        </Button>
                    )}
                </Modal.Footer>
            );
        } else {
            return (
                <Modal.Footer className={footerClassName}>
                    {footer}
                </Modal.Footer>
            );
        }
    }

    render() {
        const { modalDescription } = this.props;

        const modalProps = {
            show: this.props.show,
            container: modalContainer,
            dialogClassName: classnames('otrl-body', this.props.className, {
                'modal--flex': this.props.useFlex,
                'modal--no-header': this.props.header === false,
                [`modal--variant-${this.props.variant}`]: this.props.variant,
            }),
            onHide: this.props.onClose,
            animation: false,
            backdrop: this.props.disableBlur ? 'static' : true,
            keyboard: !this.props.disableEscape,
            backdropClassName: this.props.backdropClassName,
            bodyAriaLabel: this.props.bodyAriaLabel,
            manager: modalManager,
            'aria-modal': true,
            'aria-hidden': !this.props.show,
            'aria-labelledby': !this.props.header && this.props.titleText ? 'modalTitle' : null,
            'aria-describedby': modalDescription ? 'modalDescription' : null,
        };

        return (
            <Modal {...modalProps}>
                <BaseModalTracker {...this.props} />

                {modalDescription && <span id="modalDescription" className="sr-only">{ modalDescription }</span>}

                {!this.props.hideHeader && this._renderHeader(this.props)}

                <Modal.Body aria-label={this.props.bodyAriaLabel} className={classnames({ 'modal-body--flex': this.props.useFlex })} onKeyDown={this.props.onKeyDown} id={this.uid} tabIndex="0">
                    {this.props.children}
                </Modal.Body>

                {this._renderFooter(this.props)}
            </Modal>
        );
    }

}

export default connectToStores(
    [DimensionsStore],
    _getStateFromStores,
)(BaseModal);



// WEBPACK FOOTER //
// ./src/components/general/BaseModal.js